/**
 * A2P 10DLC validation rules + canonical compliance text.
 *
 * Rules ported from a2p-service/test/HANDOFF.md and the prototype's data.jsx.
 * Carriers reject registrations for predictable reasons — shared-inbox rep
 * emails, non-ownership titles, malformed EINs, missing compliance language.
 * These validators surface those failures in the form before submission.
 */

export const BUSINESS_TYPES = [
  'Limited Liability Corporation (LLC)',
  'Corporation',
  'Sole Proprietorship',
  'Partnership',
  'Non-profit Corporation',
  'Co-operative'
];

export const REP_TITLES_ALLOWED = [
  'Owner',
  'CEO',
  'President',
  'Managing Member',
  'Managing Partner',
  'Principal'
];

export const REP_TITLES_REJECTED = [
  'Office Manager',
  'Practice Manager',
  'Practice Administrator',
  'Front Desk',
  'Receptionist'
];

export const REP_EMAIL_BLOCKED_PREFIXES = [
  'frontdesk@',
  'info@',
  'office@',
  'admin@',
  'hello@',
  'contact@',
  'hr@',
  'support@',
  'billing@',
  'reception@'
];

export const MESSAGE_FREQUENCY_OPTIONS = [
  'Up to 4 msgs/month',
  'Up to 8 msgs/month',
  'Up to 15 msgs/month',
  'Up to 30 msgs/month'
];

const ENTITY_SUFFIX_REGEX = /\b(LLC|PLLC|Inc|Corp|Ltd|PC|PA|LP|LLP)\b/i;
const US_EIN_REGEX = /^\d{2}-\d{7}$/;
const CANADIAN_BN_REGEX = /^\d{9}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const URL_REGEX = /^https?:\/\/.+\..+/;

/**
 * Returns `{ level, msg }` or `null` when there's nothing to say (empty input).
 * level: 'ok' | 'warn' | 'err'
 */
export function validateLegalName(value) {
  if (!value || value.trim().length < 3) return null;
  if (!ENTITY_SUFFIX_REGEX.test(value)) {
    return {
      level: 'warn',
      msg: "Heads up — we don't see an entity suffix (LLC, Inc, etc). Double-check this matches your filing exactly."
    };
  }
  return { level: 'ok', msg: 'Looks like a valid legal entity name.' };
}

export function validateTaxId(value, country = 'US') {
  if (!value) return null;
  const clean = String(value).replace(/\s/g, '');
  if (country === 'CA') {
    if (CANADIAN_BN_REGEX.test(clean)) {
      return { level: 'ok', msg: 'Valid 9-digit Canadian Business Number.' };
    }
    return {
      level: 'err',
      msg: 'Canadian BN must be 9 digits, no dashes (e.g. 123456789).'
    };
  }
  if (US_EIN_REGEX.test(clean)) {
    return { level: 'ok', msg: 'Valid US EIN format.' };
  }
  return {
    level: 'err',
    msg: 'US EIN must be XX-XXXXXXX (9 digits with a dash).'
  };
}

export function validateWebsite(value) {
  if (!value) return null;
  if (!URL_REGEX.test(value)) {
    return {
      level: 'err',
      msg: 'Must start with https:// or http:// and include a domain.'
    };
  }
  return { level: 'ok', msg: 'Valid URL.' };
}

export function validateRepName(value) {
  if (!value) return null;
  if (!/\s/.test(value.trim())) {
    return {
      level: 'warn',
      msg: 'Looks like just one name — we need first AND last name.'
    };
  }
  return { level: 'ok', msg: 'Good.' };
}

/**
 * Returns 'err' for known-rejected titles, 'ok' for allowed, null for empty/custom.
 * UI should present REP_TITLES_ALLOWED as a dropdown but accept free text too.
 */
export function validateRepTitle(value) {
  if (!value || value.trim() === '') return null;
  const v = value.trim();
  if (REP_TITLES_REJECTED.some((t) => v.toLowerCase().includes(t.toLowerCase()))) {
    return {
      level: 'err',
      msg: 'This title will cause automatic rejection. The rep must be ownership-level (Owner, CEO, Principal, etc.).'
    };
  }
  if (REP_TITLES_ALLOWED.some((t) => t.toLowerCase() === v.toLowerCase())) {
    return { level: 'ok', msg: 'Ownership-level title — passes verification.' };
  }
  return {
    level: 'warn',
    msg: `Recommended titles: ${REP_TITLES_ALLOWED.join(', ')}. Non-ownership titles are rejected by carriers.`
  };
}

export function validateRepEmail(value) {
  if (!value) return null;
  if (!EMAIL_REGEX.test(value)) {
    return { level: 'err', msg: 'Not a valid email format.' };
  }
  const lower = value.toLowerCase();
  if (REP_EMAIL_BLOCKED_PREFIXES.some((p) => lower.startsWith(p))) {
    return {
      level: 'err',
      msg: 'Shared inbox — will be rejected. Use the owner\'s personal email.'
    };
  }
  return { level: 'ok', msg: 'Looks like a personal email — good.' };
}

export function validatePhone(value) {
  if (!value) return null;
  const digits = String(value).replace(/\D/g, '');
  if (digits.length === 10) {
    return { level: 'ok', msg: 'Valid 10-digit phone.' };
  }
  if (digits.length === 11 && digits[0] === '1') {
    return { level: 'ok', msg: 'Valid US/CA number with country code.' };
  }
  return { level: 'err', msg: `Need 10 digits — got ${digits.length}.` };
}

export function validateOfficeEmail(value) {
  if (!value) return null;
  if (!EMAIL_REGEX.test(value)) {
    return { level: 'err', msg: 'Not a valid email format.' };
  }
  return { level: 'ok', msg: 'Valid email.' };
}

// ---- Compliance blocks ----
// Canonical text that carriers expect to find on the practice's privacy policy
// and terms of service pages. {{OFFICE_PHONE}}, {{OFFICE_EMAIL}}, and
// {{MESSAGE_FREQUENCY}} are substituted with the practice's values before
// display / copy / email.
//
// ⚠️ KEEP IN SYNC with teamio-serverless-api/a2p-service/compliance-pages-generator.js.
// That file generates the same two blocks for the email path; this file is what
// the WIZARD shows customers to copy-paste. They are the same compliance text
// delivered by two routes, and they must not drift.
//
// The **Privacy.** sentence is LOAD-BEARING and must equal, character for character,
// SMS_PRIVACY_SENTENCE in teamio-serverless-api/a2p-service/sms-privacy-language.js
// (a2p-sync-check.js enforces it; this file cannot import across the repo boundary).
// History: added 2026-08-30 as a "Mobile opt-in data ... never shared, sold, or
// transferred to any third party or affiliate" clause after Family EcoDental was
// rejected TWICE with Twilio 30882 (policy must make clear opt-in information is not
// shared with third parties or affiliates). REPLACED 2026-09-15 with Twilio's exact
// 30908 wording after Brester Dentistry was rejected 30908 on 2026-09-14 for copying
// the older "kept confidential ... not shared with third parties for marketing
// purposes" line, which this block still carried NEXT TO the 2026-08-30 clause — two
// differently-worded statements, itself a listed 30908 cause. One sentence, stated
// once per block. Do not weaken, reword or duplicate it.

export const PRIVACY_BLOCK = `**Text Message Communications (SMS Program)**

**Consent.** With your express consent, we may send text messages to your mobile phone number for:
• Appointment reminders and confirmations
• Treatment follow-up instructions
• Important health and care updates related to your treatment
• Prescription and care reminders

**Message Frequency.** Message frequency varies but typically {{MESSAGE_FREQUENCY}}. You may receive additional messages during active treatment periods.

**Costs.** Message and data rates may apply as determined by your mobile carrier. We do not charge for text messages, but your carrier's standard messaging rates will apply.

**Opt-Out.** You can opt out of text messages at any time by:
• Replying STOP to any text message
• Calling our office at {{OFFICE_PHONE}}
• Requesting removal during your next visit
• Emailing us at {{OFFICE_EMAIL}}

**Help.** For help with text messages, reply HELP to any message or contact our office directly.

**Privacy.** We do not share, sell, or provide your mobile phone number or messaging consent data to third parties or affiliates for marketing or promotional purposes.`;

export const TERMS_BLOCK = `**SMS Text Messaging Program**

We offer an SMS text messaging program for appointment reminders and care coordination. By providing your mobile phone number and opting in:
• You expressly consent to receive text messages at the number provided
• You confirm that you are the owner or authorized user of the mobile phone number
• You understand that consent is not required as a condition of receiving healthcare services
• Message and data rates may apply as determined by your mobile carrier

**Message Types.** Appointment reminders, treatment follow-ups, care updates, and care instructions for our dental patients.

**Frequency.** Typically {{MESSAGE_FREQUENCY}}. During active treatment periods, you may receive additional messages.

**Opt-Out Instructions.** You can opt out at any time by:
• Replying STOP to any message you receive from us
• Calling our office at {{OFFICE_PHONE}}
• Emailing us at {{OFFICE_EMAIL}}
• Informing our staff during your next visit

**Help and Support.** For help, reply HELP to any message or call our office at {{OFFICE_PHONE}}. You can also email us at {{OFFICE_EMAIL}} for customer support.

**Privacy.** We do not share, sell, or provide your mobile phone number or messaging consent data to third parties or affiliates for marketing or promotional purposes. For details on how we handle your information, see our Privacy Policy.

**Limitations.** SMS messages should not be used for urgent medical needs or emergencies. Message delivery is not guaranteed and may be delayed. Carriers are not liable for any delayed or undelivered messages.`;

export function substituteCompliance(block, { officePhone, officeEmail, messageFrequency } = {}) {
  return block
    .replace(/\{\{OFFICE_PHONE\}\}/g, officePhone || '[OFFICE PHONE]')
    .replace(/\{\{OFFICE_EMAIL\}\}/g, officeEmail || '[OFFICE EMAIL]')
    .replace(/\{\{MESSAGE_FREQUENCY\}\}/g, messageFrequency || 'up to 8 msgs/month');
}

// 11-item carrier compliance checklist — which block each item should appear in.
// Used both for displaying expected items and for rendering server-scan results.
export const COMPLIANCE_CHECKLIST = [
  { id: 'rates',         label: 'Message and data rates may apply',      privacy: true,  terms: true  },
  { id: 'stop',          label: 'STOP opt-out instructions',             privacy: true,  terms: true  },
  { id: 'help',          label: 'HELP support instructions',             privacy: true,  terms: true  },
  { id: 'frequency',     label: 'Message frequency disclosure',          privacy: true,  terms: true  },
  { id: 'consent',       label: 'SMS consent language',                  privacy: true,  terms: false },
  { id: 'notRequired',   label: '"Not required as a condition"',         privacy: false, terms: true  },
  { id: 'notShared',     label: 'Not shared with third parties',         privacy: true,  terms: false },
  { id: 'msgTypes',      label: 'Message types / program description',   privacy: false, terms: true  },
  { id: 'carriersNotLiable', label: '"Carriers not liable" disclosure',  privacy: false, terms: true  },
  { id: 'privacyRef',    label: 'Link/reference to privacy policy',      privacy: false, terms: true  },
  { id: 'supportInfo',   label: 'Customer support contact info',         privacy: false, terms: true  }
];
